import type { ClientConnectCustomFieldSet } from '../../../../Settings/ClientConnectFirmSettings/CustomFieldSets/types';

export type PortalCustomFieldPayload = {
    name: string;
    type: string;
    perClient: boolean;
    value: string;
    identifier?: string;
    intakeForm?: string | null;
};

export function expandTemplateNamesToFields(
    selectedSetNames: string[],
    savedCustomFieldSets: ClientConnectCustomFieldSet[]
): PortalCustomFieldPayload[] {
    const raw: PortalCustomFieldPayload[] = [];
    for (let i = 0; i < selectedSetNames.length; i++) {
        for (let j = 0; j < savedCustomFieldSets.length; j++) {
            if (selectedSetNames[i] === savedCustomFieldSets[j].fieldSetName) {
                const fields = savedCustomFieldSets[j].fields;
                const templateName = savedCustomFieldSets[j].fieldSetName;
                for (let k = 0; k < fields.length; k++) {
                    const f = fields[k];
                    raw.push({
                        name: f.name,
                        type: f.type,
                        perClient: !!f.perClient,
                        value: f.value ?? '',
                        identifier: f.identifier,
                        intakeForm: templateName
                    });
                }
            }
        }
    }
    return raw;
}

export function removeDuplicateCustomFields<T extends { name: string }>(submittedCustomFieldValues: T[]): T[] {
    if (submittedCustomFieldValues.length === 0) {
        return [];
    }
    const arr = submittedCustomFieldValues;
    for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length; j++) {
            if (arr[i].name.toLowerCase() === arr[j].name.toLowerCase()) {
                arr.splice(j, 1);
            }
        }
    }
    return arr;
}

function fieldKey(name: string, perClient: boolean): string {
    return `${name.toLowerCase()}|${perClient}`;
}

export function keysForExpandedTemplates(
    selectedTemplateNames: string[],
    firmSets: ClientConnectCustomFieldSet[]
): Set<string> {
    const expanded = expandTemplateNamesToFields(selectedTemplateNames, firmSets);
    const keys = new Set<string>();
    for (const f of expanded) {
        keys.add(fieldKey(f.name, f.perClient));
    }
    return keys;
}

function mapApiRowToPayload(row: {
    Name: string;
    Type: string;
    Value: string;
    PerClient: boolean;
    Identifier?: string;
    IntakeForm?: string | null;
}): PortalCustomFieldPayload {
    return {
        name: row.Name,
        type: row.Type,
        perClient: row.PerClient,
        value: row.Value ?? '',
        identifier: row.Identifier,
        intakeForm: row.IntakeForm ?? null
    };
}

export function distinctIntakeFormNamesFromPortalRows(
    rows: Array<{ IntakeForm?: string | null }>
): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const row of rows) {
        const v = row.IntakeForm;
        if (v == null || String(v).trim() === '') continue;
        if (!seen.has(v)) {
            seen.add(v);
            out.push(v);
        }
    }
    return out;
}

export function buildMergedPortalCustomFields(
    prevSelectedTemplateNames: string[],
    newSelectedTemplateNames: string[],
    firmSets: ClientConnectCustomFieldSet[],
    currentPortalFields: Array<{
        Name: string;
        Type: string;
        Value: string;
        PerClient: boolean;
        Identifier?: string;
        IntakeForm?: string | null;
    }>
): PortalCustomFieldPayload[] {
    const prevKeys = keysForExpandedTemplates(prevSelectedTemplateNames, firmSets);
    const individual: PortalCustomFieldPayload[] = [];
    for (const row of currentPortalFields) {
        const p = mapApiRowToPayload(row);
        if (!prevKeys.has(fieldKey(p.name, p.perClient))) {
            individual.push(p);
        }
    }
    const newTemplateFields = expandTemplateNamesToFields(newSelectedTemplateNames, firmSets);
    const combined = [...newTemplateFields, ...individual];
    return removeDuplicateCustomFields(removeDuplicateCustomFields([...combined]));
}
